import { NSARouterMakerResult } from "namirasoft-account-react";
import { ConditionGroupLineCcgType, ConditionGroupLineInputRow, ConditionGroupLineSecondCcgType, ConditionGroupLineSecondOperandType, RouterRouteCcgType } from "namirasoft-inference";
import { IBaseComponentProps, NSBox, NSBoxEnum, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import CCGTypeID from "./CCGTypeID";
import { InferenceRouterProps } from "../InferenceRouterProps";

export interface ConditionGroupLineComponentProps extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    index: number;
    deletable: boolean;
    onDelete: () => void;
    onChanged?: (value: ConditionGroupLineInputRow | null) => void;
}

interface ConditionGroupLineComponentState
{
    second_operand_type: ConditionGroupLineSecondOperandType;
}

function needsSecondCcg(operand: ConditionGroupLineSecondOperandType): boolean
{
    return operand === ConditionGroupLineSecondOperandType.EQUAL_CCG
        || operand === ConditionGroupLineSecondOperandType.NOT_EQUAL_CCG;
}

export default class ConditionGroupLineComponent extends Component<ConditionGroupLineComponentProps, ConditionGroupLineComponentState>
{
    static IS_DIRTY(value: ConditionGroupLineInputRow | null): boolean
    {
        if (value && value.ccg_id)
            return true;
        return false;
    }

    private CCGTypeID_First = createRef<CCGTypeID>();
    private NSBoxEnum_SecondOperandType = createRef<NSBoxEnum<ConditionGroupLineSecondOperandType>>();
    private CCGTypeID_Second = createRef<CCGTypeID>();

    constructor(props: ConditionGroupLineComponentProps)
    {
        super(props);
        this.state = { second_operand_type: ConditionGroupLineSecondOperandType.TRUE };
        this.getValue = this.getValue.bind(this);
        this.isDirty = this.isDirty.bind(this);
        this.onChanged = this.onChanged.bind(this);
        this.onSecondOperandChanged = this.onSecondOperandChanged.bind(this);
    }

    getValue(checkError: boolean = false): ConditionGroupLineInputRow | null
    {
        let first = this.CCGTypeID_First.current?.getValue(checkError) ?? { ccg_type: RouterRouteCcgType.Condition, ccg_id: "" };
        let ccg_id = first.ccg_id;
        if (!ccg_id)
            return null;
        let ccg_type = first.ccg_type as unknown as ConditionGroupLineCcgType;
        let second_operand_type = this.NSBoxEnum_SecondOperandType.current?.getValueEnumOne(ConditionGroupLineSecondOperandType, ConditionGroupLineSecondOperandType.TRUE, checkError) ?? ConditionGroupLineSecondOperandType.TRUE;
        let second_ccg_type: ConditionGroupLineSecondCcgType | null = null;
        let second_ccg_id: string | null = null;
        if (needsSecondCcg(second_operand_type))
        {
            let second = this.CCGTypeID_Second.current?.getValue(checkError) ?? { ccg_type: RouterRouteCcgType.Condition, ccg_id: "" };
            second_ccg_type = second.ccg_type as unknown as ConditionGroupLineSecondCcgType;
            second_ccg_id = second.ccg_id || null;
        }
        return {
            ccg_type,
            ccg_id,
            second_operand_type,
            second_ccg_type,
            second_ccg_id,
        };
    }

    setValue(value: ConditionGroupLineInputRow)
    {
        this.CCGTypeID_First.current?.setValue({
            ccg_type: value.ccg_type as unknown as RouterRouteCcgType,
            ccg_id: value.ccg_id,
        });
        this.NSBoxEnum_SecondOperandType.current?.setValue(value.second_operand_type);
        this.setState({ second_operand_type: value.second_operand_type }, () =>
        {
            if (value.second_ccg_type || value.second_ccg_id)
            {
                this.CCGTypeID_Second.current?.setValue({
                    ccg_type: (value.second_ccg_type ?? ConditionGroupLineSecondCcgType.Condition) as unknown as RouterRouteCcgType,
                    ccg_id: value.second_ccg_id ?? "",
                });
            }
        });
    }

    isDirty(): boolean
    {
        return ConditionGroupLineComponent.IS_DIRTY(this.getValue(false));
    }

    private onChanged()
    {
        if (this.props.onChanged)
            this.props.onChanged(this.getValue(false));
    }

    private onSecondOperandChanged()
    {
        let next = this.NSBoxEnum_SecondOperandType.current?.getValueEnumOne(ConditionGroupLineSecondOperandType, ConditionGroupLineSecondOperandType.TRUE, false) ?? ConditionGroupLineSecondOperandType.TRUE;
        if (next !== this.state.second_operand_type)
            this.setState({ second_operand_type: next }, () => this.onChanged());
        else
            this.onChanged();
    }

    override render()
    {
        let showSecond = needsSecondCcg(this.state.second_operand_type);
        return (
            <NSColumn style={{ ...this.props.style }}>
                <NSRow style={{ flexWrap: "nowrap" }}>
                    <CCGTypeID
                        ref={this.CCGTypeID_First}
                        {...this.props}
                        typeTitle="Type"
                        idTitle={`ID ${this.props.index + 1}`}
                        required
                        width={NSBox.width.one}
                        onChanged={this.onChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-lines" }}
                    />
                    <NSBoxEnum<ConditionGroupLineSecondOperandType>
                        ref={this.NSBoxEnum_SecondOperandType}
                        title="Second Operand Type"
                        required
                        multiple={false}
                        reloadable={false}
                        style={{ width: NSBox.width.one, minWidth: NSBox.width.one, flexShrink: 0 }}
                        getEnumObject={() => ConditionGroupLineSecondOperandType}
                        onChanged={this.onSecondOperandChanged}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-lines" }}
                        menu={{
                            builtin: { delete: { enabled: this.props.deletable, onDelete: () => { this.props.onDelete(); } } },
                            items: []
                        }}
                    />
                </NSRow>
                {showSecond && (
                    <NSRow style={{ flexWrap: "nowrap" }}>
                        <CCGTypeID
                            ref={this.CCGTypeID_Second}
                            {...this.props}
                            typeTitle="Second Type"
                            idTitle="Second ID"
                            required
                            width={NSBox.width.one}
                            onChanged={this.onChanged}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition-group/#field-lines" }}
                        />
                    </NSRow>
                )}
            </NSColumn>
        );
    }
}